import { useParams, Link, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { SiteShell } from "@/components/site/SiteShell";
import { SEO, breadcrumbSchema, blogPostingSchema } from "@/components/site/SEO";
import { faqSchema } from "@/lib/seoSchemas";
import { Button } from "@/components/ui/button";
import { Clock, ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { BLOG_POSTS, BLOG_AUTHOR, getRelatedPosts } from "@/lib/blogData";
import { seoForPost } from "@/lib/routeSeo";

// Locked-down DOMPurify config: only anchor tags with safe attributes/protocols.
// Blog body copy is authored by us, but sanitising at the sink protects against
// any future accidental XSS if a post ever renders user- or CMS-sourced HTML.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["a", "strong", "em", "b", "i", "br", "span"],
  ALLOWED_ATTR: ["href", "class", "target", "rel", "title"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

const sanitize = (html) => DOMPurify.sanitize(html, SANITIZE_CONFIG);

const InternalLinker = ({ text }) => {
  // Auto-link key phrases to relevant pages - boost internal linking & SEO
  const links = [
    [/(videographer in Cape Town|videographer Cape Town|Cape Town videographer)/gi, "/services/videographer-cape-town"],
    [/(video production company in Cape Town|Cape Town video production company|video production company)/gi, "/services/video-production-company-cape-town"],
    [/(video production agency|video agency)/gi, "/services/video-production-agency"],
    [/(brand films?)/gi, "/services/brand-films"],
    [/(corporate video|corporate storytelling videos)/gi, "/services/corporate-video"],
    [/(post-production|colour grading|video editing)/gi, "/services/post-production"],
    [/(TVCs?|commercial video production|commercials?)/g, "/services/commercials"],
    [/(documentary filmmaking|documentary production|documentaries)/gi, "/services/documentary-filmmaking"],
  ];
  // Apply first matching pattern per span only once to avoid noise
  let html = text;
  const seen = new Set();
  for (const [pattern, href] of links) {
    html = html.replace(pattern, (match) => {
      const key = match.toLowerCase();
      if (seen.has(key)) return match;
      seen.add(key);
      return `<a class="text-[hsl(var(--primary))] hover:underline underline-offset-4 decoration-[hsl(var(--primary)/0.6)]" href="${href}">${match}</a>`;
    });
  }
  return <span dangerouslySetInnerHTML={{ __html: sanitize(html) }} />;
};

const Block = ({ b }) => {
  if (b.type === "p") return <p className="text-base sm:text-lg leading-relaxed text-foreground/85 my-5"><InternalLinker text={b.text} /></p>;
  if (b.type === "h2") return <h2 className="font-display uppercase tracking-[0.02em] text-foreground text-2xl sm:text-3xl mt-12 mb-4">{b.text}</h2>;
  if (b.type === "h3") return <h3 className="font-display uppercase tracking-[0.02em] text-foreground text-xl mt-8 mb-3">{b.text}</h3>;
  if (b.type === "list") return (<ul className="my-5 space-y-2.5 list-none">{b.items.map((it) => (<li key={it} className="flex items-start gap-3 text-base text-foreground/85 leading-relaxed"><span className="mt-2.5 inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] shrink-0" /><span><InternalLinker text={it} /></span></li>))}</ul>);
  if (b.type === "olist") return (<ol className="my-5 space-y-4 list-none counter-reset-[step]">{b.items.map((it, i) => (<li key={it.h || i} className="flex gap-4"><span className="font-display text-[hsl(var(--primary))] text-2xl leading-none w-8 shrink-0">{String(i+1).padStart(2, "0")}</span><div><div className="font-display uppercase tracking-[0.02em] text-foreground">{it.h}</div><div className="text-sm sm:text-base text-muted-foreground leading-relaxed mt-1"><InternalLinker text={it.t} /></div></div></li>))}</ol>);
  if (b.type === "image") return (<figure className="my-8"><img src={b.src} alt={b.alt || ""} className="w-full rounded-xl border border-border" loading="lazy" />{b.alt && <figcaption className="mt-2 text-xs text-muted-foreground text-center">{b.alt}</figcaption>}</figure>);
  if (b.type === "quote") return (<blockquote className="my-8 border-l-2 border-[hsl(var(--primary))] pl-5 text-lg sm:text-xl italic text-foreground/90">“{b.text}”</blockquote>);
  if (b.type === "table") return (<div className="my-8 overflow-x-auto rounded-xl border border-border"><table className="w-full text-sm"><thead className="bg-[hsl(225,16%,14%)]"><tr>{b.headers.map(h => <th key={h} className="text-left px-4 py-3 font-display uppercase tracking-[0.14em] text-xs text-foreground">{h}</th>)}</tr></thead><tbody>{b.rows.map((r) => (<tr key={r[0]} className="border-t border-border">{r.map((c, j) => (<td key={`${r[0]}-${j}`} className="px-4 py-3 text-foreground/85">{c}</td>))}</tr>))}</tbody></table></div>);
  if (b.type === "cta") return (
    <div className="my-12 rounded-2xl border border-border bg-[hsl(225,18%,9%)] p-6 sm:p-8">
      <p className="kicker mb-2">Next step</p>
      <h3 className="font-display uppercase text-foreground text-2xl sm:text-3xl tracking-[0.02em]">{b.title}</h3>
      <p className="mt-3 text-sm sm:text-base text-muted-foreground">{b.body}</p>
      <Button asChild className="mt-5 h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-5"><Link to={b.buttonHref}>{b.buttonLabel} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
    </div>
  );
  return null;
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = getRelatedPosts(post.slug, 3);

  // Build per-post SEO using the centralised helper (handles fallback for any post not in routeSeo table)
  const postSeo = seoForPost(post.slug, post.title, post.metaDescription || post.excerpt);

  return (
    <SiteShell>
      <SEO
        title={postSeo.title}
        description={postSeo.meta}
        path={`/post/${post.slug}`}
        image={post.cover}
        type="article"
        jsonLd={[
          breadcrumbSchema([{name: "Home", path: "/"}, {name: "Blog", path: "/blog"}, {name: post.title}]),
          blogPostingSchema({
            title: post.title,
            description: postSeo.meta,
            slug: post.slug,
            date: post.publishedAt || post.date || "2025-01-01",
            image: post.cover,
            authorName: post.author || (BLOG_AUTHOR && BLOG_AUTHOR.name) || "Pie in the Sky Productions",
          }),
          ...(Array.isArray(post.faqs) && post.faqs.length > 0 ? [faqSchema(post.faqs)] : []),
        ]}
      />
      <article className="pt-32 sm:pt-40">
        <header className="container-site max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground mb-8"><ArrowLeft className="h-3 w-3" /> All articles</Link>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {(post.tags || []).map(t => (<span key={t} className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.35)] rounded-full px-3 py-1">{t}</span>))}
          </div>
          <h1 className="font-display uppercase text-foreground text-[clamp(2rem,5.5vw,4.5rem)] leading-[0.95] text-balance">{post.title}</h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-xs text-muted-foreground border-t border-border pt-6">
            <div className="flex items-center gap-3"><img src={BLOG_AUTHOR.avatar} alt={BLOG_AUTHOR.name} className="h-9 w-9 rounded-full object-cover border border-border" /><div><div className="text-foreground">{BLOG_AUTHOR.name}</div><div className="mt-0.5">{BLOG_AUTHOR.role}</div></div></div>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" />{new Date(post.publishedAt).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" />{post.readMinutes} min read</span>
          </div>
        </header>
        <div className="container-site max-w-3xl mt-10">
          <figure className="mb-12"><img src={post.cover} alt={post.title} className="w-full rounded-2xl border border-border" /></figure>
          <div className="prose-style">{post.body.map((b, i) => <Block key={`${b.type}-${i}`} b={b} />)}</div>
          {Array.isArray(post.faqs) && post.faqs.length > 0 && (
            <section data-testid="post-faqs" className="mt-14 border-t border-border pt-10">
              <p className="kicker mb-3">Frequently asked questions</p>
              <h2 className="font-display uppercase tracking-[0.02em] text-foreground text-2xl sm:text-3xl mb-8">FAQs</h2>
              <div className="space-y-6">
                {post.faqs.map((f) => (
                  <details
                    key={f.question}
                    data-testid={`faq-item-${f.question.slice(0, 40).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group rounded-xl border border-border bg-card/60 open:bg-card px-5 py-4 sm:px-6 sm:py-5"
                  >
                    <summary className="cursor-pointer list-none flex items-start gap-4 font-display text-base sm:text-lg text-foreground tracking-[0.01em] leading-snug">
                      <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] shrink-0" />
                      <span className="flex-1">{f.question}</span>
                      <span
                        aria-hidden
                        className="ml-2 shrink-0 text-[hsl(var(--primary))] transition-transform group-open:rotate-45 text-xl leading-none"
                      >+</span>
                    </summary>
                    <p className="mt-3 pl-5 text-sm sm:text-base text-foreground/85 leading-relaxed">
                      <InternalLinker text={f.answer} />
                    </p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
        {related.length > 0 && (
          <section className="mt-20 section-pad bg-[hsl(225,22%,4%)] border-t border-border">
            <div className="container-site">
              <p className="kicker mb-3">Keep reading</p>
              <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl mb-10">Related articles.</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map(r => (
                  <Link key={r.slug} to={`/post/${r.slug}`} data-testid={`related-post-${r.slug}`} className="group block rounded-2xl overflow-hidden border border-border bg-card hover:bg-[hsl(225,18%,9%)] transition-colors">
                    <div className="aspect-[16/10] overflow-hidden"><img src={r.cover} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" loading="lazy" /></div>
                    <div className="p-5">
                      <h3 className="font-display text-lg uppercase tracking-[0.02em] text-foreground leading-tight">{r.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </SiteShell>
  );
}
